import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Space,
  Tag,
  Drawer,
  Switch,
  message,
  Tooltip
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import {
  fetchLeaveTypes,
  createLeaveType,
  updateLeaveType,
  deleteLeaveType,
  clearError
} from '../redux/slices/leaveTypeSlice';
import { canCreateRole, canEditRole, canDeleteRole } from '../utils/permissions';

const LeaveTypes = () => {
  const dispatch = useDispatch();
  const { list: leaveTypes, loading, error } = useSelector((state) => state.leaveTypes);
  const { user } = useSelector((state) => state.auth);
  const [form] = Form.useForm();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isViewDrawerVisible, setIsViewDrawerVisible] = useState(false);
  const [editingLeaveType, setEditingLeaveType] = useState(null);
  const [viewingLeaveType, setViewingLeaveType] = useState(null);

  useEffect(() => {
    dispatch(fetchLeaveTypes());
  }, [dispatch]);

  useEffect(() => {
    if (error) {
      message.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  const showCreateModal = () => {
    form.resetFields();
    setEditingLeaveType(null);
    setIsModalVisible(true);
  };

  const showEditModal = (record) => {
    setEditingLeaveType(record);
    form.setFieldsValue({
      name: record.name,
      maxDaysPerYear: record.maxDaysPerYear,
      description: record.description,
      carryForwardLimit: record.carryForwardLimit,
      isEncashable: record.isEncashable,

    });
    setIsModalVisible(true);
  };

  const showViewDrawer = (record) => {
    setViewingLeaveType(record);
    setIsViewDrawerVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setEditingLeaveType(null);
    form.resetFields();
  };

  const handleViewDrawerClose = () => {
    setIsViewDrawerVisible(false);
    setViewingLeaveType(null);
  };

  const handleSubmit = async (values) => {
    try {
      if (editingLeaveType) {
        await dispatch(
          updateLeaveType({
            id: editingLeaveType.id,
            data: values
          })
        ).unwrap();
        message.success('Leave type updated successfully');
      } else {
        await dispatch(createLeaveType(values)).unwrap();
        message.success('Leave type created successfully');
      }
      handleCancel();
    } catch (error) {
      message.error(error || 'Operation failed');
    }
  };

  const handleDelete = (record) => {
    Modal.confirm({
      title: 'Delete Leave Type',
      icon: <ExclamationCircleOutlined />,
      content: `Are you sure you want to delete "${record.name}"?`,
      okText: 'Yes',
      cancelText: 'No',
      onOk: async () => {
        try {
          await dispatch(deleteLeaveType(record.id)).unwrap();
          message.success('Leave type deleted successfully');
        } catch (error) {
          message.error(error || 'Failed to delete leave type');
        }
      }
    });
  };

  const columns = [
    {
      title: 'Leave Type Name',
      dataIndex: 'name',
      key: 'name',
      render: (text) => <strong>{text}</strong>
    },
    {
      title: 'max  Days Per Year',
      dataIndex: 'maxDaysPerYear',
      key: 'maxDaysPerYear',
      render: (text) => <Tag>{text}</Tag>
    },
    
    {
      title: 'Carry Forward Limit',
      dataIndex: 'carryForwardLimit',
      key: 'carryForwardLimit',
      align: 'center'
    },
    {
      title: 'is Encashable',
      dataIndex: 'isEncashable',
      key: 'isEncashable',
      align: 'center'
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: (text) => text || '-',
      ellipsis: true
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      align: 'center',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="View">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => showViewDrawer(record)}
            />
          </Tooltip>
          {canEditRole(user) && (
            <Tooltip title="Edit">
              <Button
                type="text"
                icon={<EditOutlined />}
                onClick={() => showEditModal(record)}
              />
            </Tooltip>
          )}
          {canDeleteRole(user) && (
            <Tooltip title="Delete">
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={() => handleDelete(record)}
              />
            </Tooltip>
          )}
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2>Leave Types</h2>
        {canCreateRole(user) && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={showCreateModal}
          >
            Add Leave Type
          </Button>
        )}
      </div>

      <Table
        columns={columns}
        dataSource={leaveTypes}
        loading={loading}
        rowKey="id"
        pagination={{ pageSize: 10 }}
      />

      {/* Create/Edit Modal */}
      <Modal
        title={editingLeaveType ? 'Edit Leave Type' : 'Create Leave Type'}
        visible={isModalVisible}
        onOk={form.submit}
        onCancel={handleCancel}
        confirmLoading={loading}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="name"
            label="Leave Type Name"
            rules={[{ required: true, message: 'Please enter leave type name' }]}
          >
            <Input placeholder="e.g., Casual Leave" />
          </Form.Item>

          <Form.Item
            name="maxDaysPerYear"
            label="max Days Per Year" 
            rules={[{ required: true, message: 'Please enter max days per year' }]}
          >
            <InputNumber
              min={0}
              placeholder="e.g., 10"
            />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
          >
            <Input.TextArea
              rows={3}
              placeholder="Leave type description"
            />
          </Form.Item>

           <Form.Item
            name="carryForwardLimit"
            label="Carry Forward Limit"
          >
            <InputNumber
              min={0}
              placeholder="e.g., 2"
            />
          </Form.Item>
            

          {/* <Form.Item
            name="defaultDays"
            label="Default Days"
            rules={[{ required: true, message: 'Please enter default days' }]}
          >
            <InputNumber
              min={0}
              placeholder="e.g., 5"
            />
          </Form.Item> */}

          <Form.Item
            name="isEncashable"
            label="Is Encashable"
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      {/* View Drawer */}
      <Drawer
        title="Leave Type Details"
        placement="right"
        onClose={handleViewDrawerClose}
        visible={isViewDrawerVisible}
      >
        {viewingLeaveType && (
          <div>
            <div style={{ marginBottom: '16px' }}>
              <strong>Name:</strong>
              <p>{viewingLeaveType.name}</p>
            </div>
            <div style={{ marginBottom: '16px' }}>
              <strong>Code:</strong>
              <p>
                <Tag>{viewingLeaveType.code}</Tag>
              </p>
            </div>
            <div style={{ marginBottom: '16px' }}>
              <strong>Description:</strong>
              <p>{viewingLeaveType.description || '-'}</p>
            </div>
            <div style={{ marginBottom: '16px' }}>
              <strong>Default Days:</strong>
              <p>{viewingLeaveType.defaultDays}</p>
            </div>
            <div style={{ marginBottom: '16px' }}>
              <strong>Carry Forward Limit:</strong>
              <p>{viewingLeaveType.carryForwardLimit}</p>
            </div>
            <div style={{ marginBottom: '16px' }}>
              <strong>Status:</strong>
              <p>
                <Tag color={viewingLeaveType.isActive ? 'green' : 'red'}>
                  {viewingLeaveType.isActive ? 'Active' : 'Inactive'}
                </Tag>
              </p>
            </div>
            <div style={{ marginBottom: '16px' }}>
              <strong>Created At:</strong>
              <p>{new Date(viewingLeaveType.createdAt).toLocaleString()}</p>
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
};

export default LeaveTypes;
